/**
 * «Rivedi la conversazione» — le mail del thread da cui una domanda di Aida è
 * nata, o che hanno fatto una riga del tabellone (30-09-2026).
 *
 * Enrico, guardando una domanda che diceva solo «carico avviso del 30/09»:
 * *«Marina direbbe: io non posso ricordarmi tutto!»*. Qui si rilegge la
 * conversazione, in ordine, **senza firma** (quella di Marina è in ogni mail e
 * non dice niente), con gli allegati veri da aprire.
 *
 * Il testo è della Logistica (regola del 30-08): chi non ha il permesso vede
 * quante mail ci sono e di quando, non cosa dicono — il server non gliele manda.
 */

import { ChevronDown, ChevronRight, Lock, Paperclip, X } from "lucide-react";
import { useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { useConversazione } from "@/hooks/useM3";
import { apiDownloadFile } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { MessaggioConversazione } from "@/types/api";

function quando(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** «"Giulio Poletti - R&R s.r.l." <gpoletti@…>» → «Giulio Poletti - R&R s.r.l.» */
function chi(header: string | null): string {
  if (!header) return "";
  const m = header.match(/^\s*"?([^"<]+?)"?\s*<([^>]+)>/);
  return m ? m[1].trim() : header;
}

function Messaggio({
  base,
  m,
  etichettaQuesta,
}: {
  base: string;
  m: MessaggioConversazione;
  etichettaQuesta: string;
}) {
  const [tutta, setTutta] = useState(false);
  const interno = /misterpetsrl\.com/i.test(m.da);
  return (
    <li
      className={cn(
        "rounded-lg border p-3 text-xs",
        interno ? "bg-muted/30" : "bg-sky-50/60",
        m.e_questa && "ring-2 ring-amber-300",
      )}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-semibold">{chi(m.da)}</span>
        <span className="text-[11px] text-muted-foreground">{quando(m.quando)}</span>
      </div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">
        a {m.a ? m.a.split(",").map(chi).join(", ") : "—"}
        {m.cc && <> · cc {m.cc.split(",").map(chi).filter((x) => !/^aida@/i.test(x)).join(", ")}</>}
      </div>
      {m.oggetto && <div className="mt-0.5 text-[11px] text-muted-foreground">«{m.oggetto}»</div>}
      {m.e_questa && (
        <div className="mt-1 text-[10.5px] font-semibold uppercase tracking-wide text-amber-700">
          {etichettaQuesta}
        </div>
      )}
      <p className="mt-2 whitespace-pre-line text-[12.5px] leading-relaxed">
        {m.testo || <span className="italic text-muted-foreground">(nessun testo: solo firma o allegati)</span>}
      </p>
      {m.testo_completo && m.testo_completo !== m.testo && (
        <button
          type="button"
          onClick={() => setTutta((v) => !v)}
          className="mt-1.5 inline-flex items-center gap-0.5 text-[11px] text-muted-foreground hover:text-foreground"
        >
          {tutta ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          {tutta ? "nascondi la conversazione citata" : "mostra la conversazione citata"}
        </button>
      )}
      {tutta && (
        <p className="mt-1 whitespace-pre-line border-l-2 pl-2 text-[11.5px] text-muted-foreground">
          {m.testo_completo}
        </p>
      )}
      {m.allegati.length > 0 && !m.id.startsWith("storia-") && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {m.allegati.map((a) => (
            <button
              key={a.n}
              type="button"
              onClick={() =>
                apiDownloadFile(
                  `${base}/${m.id}/allegato/${a.n}`,
                  a.nome,
                )
              }
              className="inline-flex items-center gap-1 rounded border bg-card px-1.5 py-0.5 text-[11px] hover:bg-muted"
            >
              <Paperclip className="h-3 w-3" /> {a.nome}
            </button>
          ))}
        </div>
      )}
    </li>
  );
}

/** Da una domanda (`domandaId`) o da una riga del tabellone (`rigaId`, 30-09):
 *  stessa lettura, stesse regole di visibilità. */
export function ConversazioneDrawer({
  domandaId,
  rigaId,
  titolo,
  onClose,
}: {
  domandaId?: string;
  rigaId?: string;
  titolo: string;
  onClose: () => void;
}) {
  const base = rigaId
    ? `/api/v1/m3/carichi-previsti/${rigaId}/conversazione`
    : `/api/v1/m3/domande/${domandaId}/conversazione`;
  const { data, isLoading, isError, error } = useConversazione(base);
  return (
    <div className="fixed inset-0 top-14 z-50 flex justify-end" role="dialog">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} aria-hidden />
      <div className="relative z-10 flex h-full w-full max-w-2xl flex-col overflow-y-auto border-l bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="min-w-0">
            <h3 className="font-semibold">La conversazione</h3>
            <p className="truncate text-xs text-muted-foreground">{titolo}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-muted" aria-label="Chiudi">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 p-4">
          {isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : isError ? (
            <p className="text-sm text-red-600">{(error as Error)?.message ?? "Non riuscito."}</p>
          ) : !data?.visibile ? (
            <div className="flex items-start gap-2 rounded-md border border-dashed bg-muted/30 p-3 text-xs text-muted-foreground">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                {data?.n_messaggi ?? 0} {data?.n_messaggi === 1 ? "mail" : "mail"} in questa conversazione
                {data?.ultimo_il && `, l'ultima il ${new Date(data.ultimo_il).toLocaleDateString("it-IT")}`}.
                Il testo è visibile solo alla Logistica.
              </span>
            </div>
          ) : data.messaggi.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nessuna mail collegata a {rigaId ? "questa riga" : "questa domanda"}.
            </p>
          ) : (
            <ul className="space-y-2">
              {data.messaggi.map((m) => (
                <Messaggio
                  key={m.id}
                  base={base}
                  m={m}
                  etichettaQuesta={rigaId ? "l'ultima mail della riga" : "la mail da cui nasce la domanda"}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
